<template>
  <Modal
    :modal-types="[CREATE_PRICE_ALERT_MODAL]"
    font-awesome-icon-class="fal fa-fw fa-bell-plus"
    modal-title="Create Alert"
    :should-close-modal-on-background-click="false"
  >
    <div
      v-show="isInitializingModal"
      class="loading-spinner-container"
    ><i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i></div>
    <form
      v-show="!isInitializingModal"
      class="create-price-alert-form"
      @submit.prevent="handleCreatePriceAlertFormSubmit()"
    >
      <div
        v-if="isAccountLevelsMaximumLimitsInitialized && maximumNumberOfPriceAlertsAllowed > 0"
        class="price-alert-usage-message"
      >
        <span
          v-if="!hasReachedMaximumNumberOfPriceAlerts"
        >You have used {{ currentNumberOfPriceAlerts }} of your {{ maximumNumberOfPriceAlertsAllowed }} price alerts.</span>
        <span
          v-else
        >You have used all {{ maximumNumberOfPriceAlertsAllowed }} of your price alerts.</span>
        <a
          class="price-alert-help-link"
          href="//help.stockcharts.com/scanning-and-alerts/technical-alert-workbench"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i class="fas fa-fw fa-question-circle"></i>
          Help
        </a>
      </div>
      <div
        v-if="isAccountLevelsMaximumLimitsInitialized && maximumNumberOfPriceAlertsAllowed > 0 && hasReachedMaximumNumberOfPriceAlerts && !isPro"
        class="account-upgrade-message"
      >
        Unlock more alerts when you upgrade your StockCharts service&nbsp;level
        <br />
        <a
          class="sc3-button sc3-button-blue sc3-button-medium"
          href="/youracct/servicelevel"
          target="_blank"
        >
          Upgrade Now
          <i class="fas fa-fw fa-arrow-circle-right"></i>
        </a>
      </div>
      <label class="sc3-form-label" for="create-price-alert-symbol-input">CREATE NEW ALERT FOR</label>
      <input
        type="text"
        placeholder="Enter Symbol Here"
        id="create-price-alert-symbol-input"
        class="sc3-text-input"
        autocomplete="off"
        disabled
        v-model="priceAlertSymbol"
        @blur="handleBlurPriceAlertSymbolInput()"
      />
      <div class="create-price-alert-modal-ui-columns">
        <div class="trigger-column">
          <label class="sc3-form-label">TRIGGER</label>
          <label
            class="sc3-toggle-button sc3-toggle-button-form-styled sc3-toggle-button-first"
            :class="{
              'sc3-toggle-button-active': crossesAboveOrBelow === CROSSES_ABOVE,
              'sc3-toggle-button-right-adjacent-active': crossesAboveOrBelow === CROSSES_BELOW,
              'sc3-toggle-button-disabled': isUIDisabled
            }"
          >
            <input
              type="radio"
              name="crossesAboveOrBelow"
              v-model="crossesAboveOrBelow"
              :value="CROSSES_ABOVE"
              :disabled="isUIDisabled"
            />
            Crosses Above
          </label>
          <label
            class="sc3-toggle-button sc3-toggle-button-form-styled sc3-toggle-button-last"
            :class="{
              'sc3-toggle-button-active': crossesAboveOrBelow === CROSSES_BELOW,
              'sc3-toggle-button-left-adjacent-active': crossesAboveOrBelow === CROSSES_ABOVE,
              'sc3-toggle-button-disabled': isUIDisabled
            }"
          >
            <input
              type="radio"
              name="crossesAboveOrBelow"
              v-model="crossesAboveOrBelow"
              :value="CROSSES_BELOW"
              :disabled="isUIDisabled"
            />
            Crosses Below
          </label>
        </div>
        <div class="price-container">
          <div class="price-column">
            <label class="sc3-form-label" for="create-price-alert-price-input">PRICE</label>
            <input
              type="text"
              placeholder="Enter Price"
              id="create-price-alert-price-input"
              class="sc3-text-input"
              autocomplete="off"
              :disabled="isUIDisabled"
              v-model="priceAlertPrice"
            />
          </div>
          <div class="price-percent-menu-column">
            <PriceAlertPercentageDropdownMenu @change:percentage-value="handlePercentageValueSelect" />
          </div>
        </div>
      </div>
      <label
        class="sc3-form-label"
        for="price-alert-modal-message-textarea"
      >MESSAGE</label>
      <textarea
        placeholder="Add a message that you'd like us to include when this alert is triggered (2048 characters max)"
        id="price-alert-modal-message-textarea"
        class="sc3-textarea"
        v-model="priceAlertMessage"
        :disabled="isUIDisabled"
      ></textarea>
      <label class="sc3-form-label">NOTIFICATION TYPE</label>
      <div class="sc3-form-checkbox-container">
        <SCThreeCheckbox
          id="create-price-alert-modal-email-notification-checkbox"
          v-model:checkbox-value="isEmailNotification"
          label-text="Email"
          :is-label-close-to-icon="true"
          :is-square="true"
          :is-double-spaced="true"
          :is-disabled="isUIDisabled"
        />
        <SCThreeCheckbox
          id="create-price-alert-modal-text-message-notification-checkbox"
          v-model:checkbox-value="isTextMessageNotification"
          label-text="Text Message"
          :is-label-close-to-icon="true"
          :is-square="true"
          :is-double-spaced="true"
          :is-disabled="isUIDisabled"
        />
        <SCThreeCheckbox
          id="create-price-alert-modal-site-notification-checkbox"
          v-model:checkbox-value="isSiteNotification"
          label-text="Site Notification"
          :is-label-close-to-icon="true"
          :is-square="true"
          :is-double-spaced="true"
          :is-disabled="isUIDisabled"
        />
      </div>
      <hr class="sc3-form-divider" />
      <div v-show="errorMessage" class="error-message">{{ errorMessage }}</div>
      <button
        type="submit"
        class="sc3-button sc3-button-green"
        :disabled="isUIDisabled"
      >
        <i v-show="!isCreatingPriceAlert" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isCreatingPriceAlert" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Save Alert
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { useAlerts } from '@/composables/useAlerts';
  import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
  import { fetchQuote } from '@/composables/fetchQuote';
  import { isValidNumber } from '@/utils/isValidNumber';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import PriceAlertPercentageDropdownMenu from '@/components/dropdowns/PriceAlertPercentageDropdownMenu.vue';
  import Modal from '@/components/modals/Modal.vue';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { modalType } = storeToRefs(layoutStore);
  const { isPro } = useAuthentication();
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { CREATE_PRICE_ALERT_MODAL, closeModal } = useModals();
  const {
    currentNumberOfPriceAlerts,
    hasReachedMaximumNumberOfPriceAlerts,
    createPriceAlert,
    loadAdvancedAlertsAndPriceAlerts
  } = useAlerts();
  const { isAccountLevelsMaximumLimitsInitialized, maximumNumberOfPriceAlertsAllowed } = useAccountLevelsMaximumLimits();
  const CROSSES_ABOVE = 'crossesAbove';
  const CROSSES_BELOW = 'crossesBelow';
  const isInitializingModal = ref(false);
  const isCreatingPriceAlert = ref(false);
  const errorMessage = ref('');
  const priceAlertSymbol = ref('');
  const crossesAboveOrBelow = ref(CROSSES_ABOVE);
  const priceAlertPrice = ref('');
  const priceAlertMessage = ref('');
  const isEmailNotification = ref(false);
  const isTextMessageNotification = ref(false);
  const isSiteNotification = ref(true);
  const closePrice = ref(0);
  const lastBlurredFormattedSymbol = ref('');
  let lastModalOpenedTime = -1;
  const formattedPriceAlertSymbol = computed(() => {
    return priceAlertSymbol.value
      .replace(/\s/g, '') // remove all whitespace
      .replace(/^[\-_]+/g, '') // remove leading hyphens from inverted symbols and leading underscores from unadjusted symbols
      .toUpperCase();
  });
  const isUIDisabled = computed(() => {
    return isCreatingPriceAlert.value || hasReachedMaximumNumberOfPriceAlerts.value;
  });

  interface CreatePriceAlertModalState {
    modalType: string;
    lastModalOpenedTime: number;
  };

  async function handleCreatePriceAlertFormSubmit() {
    if (
      isInitializingModal.value ||
      isCreatingPriceAlert.value ||
      hasReachedMaximumNumberOfPriceAlerts.value ||
      modalType.value !== CREATE_PRICE_ALERT_MODAL
    ) {
      return;
    }

    isCreatingPriceAlert.value = true;

    clearErrorMessage();

    const price = typeof priceAlertPrice.value === 'string' ? priceAlertPrice.value.trim() : '';
    const message = typeof priceAlertMessage.value === 'string' ? priceAlertMessage.value.trim() : '';
    const modalState = getModalState();

    if (!formattedPriceAlertSymbol.value) {
      errorMessage.value = 'Please enter a symbol.';
      isCreatingPriceAlert.value = false;
      return;
    }

    if (!isValidNumber(price)) {
      errorMessage.value = 'Please enter a valid price.';
      isCreatingPriceAlert.value = false;
      return;
    }

    if (message.length > 2048) {
      errorMessage.value = 'Message may not be longer than 2048 characters.';
      isCreatingPriceAlert.value = false;
      return;
    }

    if (!isEmailNotification.value && !isTextMessageNotification.value && !isSiteNotification.value) {
      errorMessage.value = 'Please select a notification type.';
      isCreatingPriceAlert.value = false;
      return;
    }

    try {
      await createPriceAlert(
        formattedPriceAlertSymbol.value,
        price,
        crossesAboveOrBelow.value !== CROSSES_BELOW,
        message,
        isEmailNotification.value,
        isTextMessageNotification.value,
        isSiteNotification.value
      );

      await loadAdvancedAlertsAndPriceAlerts();

      addGreenActionResultMessage('Success! Your alert has been created!');
    } catch (e) {
      window.sc3WorkbenchLogger.error('Failed to create price alert.', e);

      if (!hasModalStateChanged(modalState)) {
        addRedActionResultMessage('Failed to create alert. Please try again.');
      }
    }

    if (!hasModalStateChanged(modalState)) {
      closeModal();
    }
  }

  function getModalState(): CreatePriceAlertModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime
    };
  }

  function hasModalStateChanged(modalState: CreatePriceAlertModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  function handleBlurPriceAlertSymbolInput() {
    lastBlurredFormattedSymbol.value = formattedPriceAlertSymbol.value;
  }

  function resetModalState() {
    isInitializingModal.value = false;
    isCreatingPriceAlert.value = false;
    priceAlertSymbol.value = '';
    lastBlurredFormattedSymbol.value = '';
    crossesAboveOrBelow.value = CROSSES_ABOVE;
    priceAlertPrice.value = '';
    priceAlertMessage.value = '';
    isEmailNotification.value = false;
    isTextMessageNotification.value = false;
    isSiteNotification.value = true;
    closePrice.value = 0;

    clearErrorMessage();
  }

  async function loadClosePrice() {
    const modalState = getModalState();
    const symbol = lastBlurredFormattedSymbol.value;
    closePrice.value = 0;

    if (!symbol) {
      priceAlertPrice.value = '';
      return;
    }

    try {
      const quote = await fetchQuote(symbol);

      if (
        symbol !== lastBlurredFormattedSymbol.value ||
        hasModalStateChanged(modalState)
      ) {
        return;
      }

      closePrice.value = Number(quote.close);
      priceAlertPrice.value = `${closePrice.value.toFixed(2)}`;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Error while loading close price for symbol: ${symbol} from CreatePriceAlertModal.`, e);

      if (symbol === lastBlurredFormattedSymbol.value) {
        priceAlertPrice.value = '';
      }
    }
  }

  async function handleCreatePriceAlertModalOpened() {
    resetModalState();

    isInitializingModal.value = true;
    const modalState = getModalState();

    await loadAdvancedAlertsAndPriceAlerts();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    priceAlertSymbol.value = chartStore.symbol.replace(/^[\-_]+/g, ''); // remove leading hyphens from inverted symbols and leading underscores from unadjusted symbols
    lastBlurredFormattedSymbol.value = formattedPriceAlertSymbol.value;
    isInitializingModal.value = false;
  }

  async function handleModalTypeChanged() {
    if (modalType.value !== CREATE_PRICE_ALERT_MODAL) {
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleCreatePriceAlertModalOpened();
  }

  function clearErrorMessage() {
    errorMessage.value = '';
  }

  async function handleFormattedSymbolChanged() {
    await loadClosePrice();
  }

  function handlePercentageValueSelect(value: number) {
    if (isUIDisabled.value || !closePrice.value) {
      return;
    }

    const adjustedPrice = closePrice.value * (1 + value / 100);
    priceAlertPrice.value = adjustedPrice.toFixed(2);
  }

  watch(priceAlertSymbol, clearErrorMessage);
  watch(lastBlurredFormattedSymbol, handleFormattedSymbolChanged);
  watch(crossesAboveOrBelow, clearErrorMessage);
  watch(priceAlertPrice, clearErrorMessage);
  watch(priceAlertMessage, clearErrorMessage);
  watch(isEmailNotification, clearErrorMessage);
  watch(isTextMessageNotification, clearErrorMessage);
  watch(isSiteNotification, clearErrorMessage);
  watch(modalType, handleModalTypeChanged);

  onMounted(resetModalState);
</script>

<style scoped>
  .loading-spinner-container,
  .create-price-alert-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
    font-size: 0px;
  }

  .price-alert-usage-message {
    display: flex;
    justify-content: space-between;
    margin: 0px 0px 15px 0px;
    padding: 5px;
    background-color: #f2f5f7;
    border: 1px solid #dfe7ec;
    border-radius: 5px;
    text-align: left;
    font-size: 14px;
    color: #707f8f;
  }

  .account-upgrade-message {
    margin: 0px 0px 15px 0px;
    padding: 5px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
    color: #31708f;
    background-color: #d9edf7;
    border: 1px solid #bce8f1;
    border-radius: 4px;
  }

  .price-alert-help-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    font-weight: 700;
    color: #0f4d8a;
  }

  .price-alert-help-link:hover,
  .price-alert-help-link:active,
  .price-alert-help-link:focus {
    color: #3281d0;
  }

  .create-price-alert-modal-ui-columns {
    display: flex;
    flex-wrap: wrap;
    margin: 0px 0px 15px 0px;
    padding: 0px;
  }

  .trigger-column,
  .price-column {
    display: inline-block;
    vertical-align: bottom;
    text-align: left;
  }

  .price-container {
    display: flex;
    align-items: end;
  }

  .trigger-column {
    white-space: nowrap;
    flex: 3;
  }

  .price-column {
    flex: 2;
  }

  .price-percent-menu-column {
    display: inline-block;
    vertical-align: bottom;
    width: 40px;
    position: relative;
  }

  .price-column input[type=text].sc3-text-input {
    margin-bottom: 0px;
  }

  .error-message {
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    font-weight: 700;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }
</style>
